import { useId, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PrismSource } from "./prism-retrieval-constants"
import type { FilthSearchActivityState } from "../../utils/filth-search-activity"

import { List, Lock } from "@/components/icons"
import { cn } from "@/lib/utils"

import { PrismAccessPanel } from "./prism-access-panel"
import { PrismDepthPanel } from "./prism-depth-panel"
import { PrismFlowRow } from "./prism-flow-row"
import { PrismFocusPanel } from "./prism-focus-panel"

type PrismLevelsProps = {
  state: FilthSearchActivityState
  sources: PrismSource[]
  isLive: boolean
  /** The answer's citation click handler; makes each named source a button. */
  onSourceClick?: (citationIndex: number) => void
}

const TOGGLE_CLASS =
  "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foreground"

/**
 * Nivåer: the flow row always, the focus panel by default, and two buttons
 * that open the depth view ("All sources") and the access view
 * ("Permissions") on demand. A panel opened after the turn has ended renders
 * its final state; it never replays the live sequence.
 */
export function PrismLevels({ state, sources, isLive, onSourceClick }: PrismLevelsProps) {
  const { t } = useTranslation()
  const [depthOpen, setDepthOpen] = useState(false)
  const [accessOpen, setAccessOpen] = useState(false)
  const depthId = useId()
  const accessId = useId()

  return (
    <div className="grid gap-2.5" data-testid="prism-levels">
      <PrismFlowRow isLive={isLive} state={state} />
      <PrismFocusPanel
        isLive={isLive}
        sources={sources}
        state={state}
        onSourceClick={onSourceClick}
      />
      <div className="flex flex-wrap gap-2">
        <button
          aria-controls={depthId}
          aria-expanded={depthOpen}
          type="button"
          className={cn(
            TOGGLE_CLASS,
            depthOpen ? "border-foreground bg-primary text-primary-foreground" : "text-foreground"
          )}
          onClick={() => setDepthOpen((open) => !open)}
        >
          <List aria-hidden="true" className="size-3.5" />
          {t("activity_messages.filth.allSources", { defaultValue: "All sources" })}
        </button>
        <button
          aria-controls={accessId}
          aria-expanded={accessOpen}
          type="button"
          className={cn(
            TOGGLE_CLASS,
            accessOpen ? "border-foreground bg-primary text-primary-foreground" : "text-foreground"
          )}
          onClick={() => setAccessOpen((open) => !open)}
        >
          <Lock aria-hidden="true" className="size-3.5" />
          {t("activity_messages.filth.permissions", { defaultValue: "Permissions" })}
        </button>
      </div>
      <div hidden={!depthOpen} id={depthId}>
        {depthOpen && (
          <PrismDepthPanel
            isLive={isLive}
            sources={sources}
            state={state}
            onSourceClick={onSourceClick}
          />
        )}
      </div>
      <div hidden={!accessOpen} id={accessId}>
        {accessOpen && (
          <PrismAccessPanel
            isLive={isLive}
            sources={sources}
            state={state}
            onSourceClick={onSourceClick}
          />
        )}
      </div>
    </div>
  )
}
